import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Chip, Avatar, Divider } from "@mui/joy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { statesList } from "../../StateCodes";
import BillDetailSkeleton from "../../components/BillDetailSkeleton/BillDetailSkeleton";
import HeroImg from "../../unseen-histories-_4us-wUwO30-unsplash.jpg";
import avatarFallback from "../../Male_Avatar.jpg";
import "./StateBillDetail.css";

const ARRAY_FIELDS = ["whoItAffects", "tags", "sponsors", "votes", "history", "subjects", "progress"];

const BILL_TYPE_LABELS = {
  B: "Bill", R: "Resolution", CR: "Concurrent Resolution",
  JR: "Joint Resolution", JRCA: "Constitutional Amendment",
  EO: "Executive Order", CA: "Constitutional Amendment",
  Ref: "Referendum",
};

function formatDate(dateString) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function parseJsonField(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { return JSON.parse(val); } catch { return []; }
  }
  return [];
}

function normalizeBill(bill) {
  if (!bill) return bill;
  const result = { ...bill };
  for (const field of ARRAY_FIELDS) {
    if (field in result) result[field] = parseJsonField(result[field]);
  }
  return result;
}

function BillProgressState({ progress }) {
  if (!progress || progress.length === 0) return null;

  const eventNums = new Set(progress.map(p => Number(p.event)));
  const isTerminalSuccess = eventNums.has(4) || eventNums.has(7) || eventNums.has(8);
  const isVetoed = eventNums.has(5);
  const isFailed = eventNums.has(6);
  const isTerminalFail = isVetoed || isFailed;
  const finalLabel = isVetoed ? "Vetoed" : isFailed ? "Failed" : isTerminalSuccess ? "Enacted" : "Pending";

  const steps = [
    { label: "Introduced", completed: eventNums.has(1), failed: false },
    { label: "Passed Chamber", completed: eventNums.has(2), failed: false },
    { label: "Enrolled", completed: eventNums.has(3), failed: false },
    { label: finalLabel, completed: isTerminalSuccess, failed: isTerminalFail },
  ];

  return (
    <Box>
      <Typography component="p" sx={{
        fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
        textTransform: "uppercase", letterSpacing: "0.12em", mb: "24px",
      }}>
        Bill Progress
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0, flex: "0 0 auto" }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "50%",
                backgroundColor: step.failed ? "#c1121f" : step.completed ? "#0075b2" : "transparent",
                border: (step.failed || step.completed) ? "none" : "2px solid rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                {(step.completed || step.failed) && (
                  <Box component="span" sx={{ color: "white", fontSize: "13px", lineHeight: 1 }}>
                    {step.failed ? "✕" : "✓"}
                  </Box>
                )}
              </Box>
              <Typography sx={{
                fontSize: "11px", fontWeight: 500, mt: "8px", textAlign: "center",
                px: "4px", lineHeight: 1.3, maxWidth: "72px",
                color: step.completed ? "white" : step.failed ? "#f4a0a0" : "rgba(255,255,255,0.35)",
              }}>
                {step.label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{
                flex: 1, height: "2px", mt: "13px",
                backgroundColor: steps[i].completed ? "#0075b2" : "rgba(255,255,255,0.12)",
              }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function VoteResults({ votes }) {
  if (!votes || votes.length === 0) return null;

  return (
    <Box>
      <Typography component="p" sx={{
        fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
        textTransform: "uppercase", letterSpacing: "0.12em", mb: "20px",
      }}>
        Vote Results
      </Typography>
      <Stack spacing="28px">
        {votes.map((vote, i) => {
          const yea = Number(vote.yea) || 0;
          const nay = Number(vote.nay) || 0;
          const nv = Number(vote.nv) || 0;
          const absent = Number(vote.absent) || 0;
          const total = Number(vote.total) || yea + nay + nv + absent || 1;
          const yeaPct = Math.round((yea / total) * 100);
          const nayPct = Math.round((nay / total) * 100);
          const chamber = vote.chamber === "H" ? "House" : vote.chamber === "S" ? "Senate" : (vote.chamber || "");
          const passed = vote.passed === 1 || vote.passed === true || vote.passed === "1";

          return (
            <Box key={i}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing="12px" sx={{ mb: "12px" }}>
                <Box>
                  <Typography sx={{ fontSize: "17px", fontWeight: 500, color: "rgba(255,255,255,0.9)", lineHeight: 1.35 }}>
                    {vote.desc || `${chamber} Vote`}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", mt: "2px" }}>
                    {[chamber, formatDate(vote.date)].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                <Chip size="sm" sx={{
                  backgroundColor: passed ? "rgba(45,90,45,0.55)" : "rgba(193,18,31,0.35)",
                  color: passed ? "#a8d5a2" : "#f4a0a0",
                  border: "none", fontWeight: 600, fontSize: "11px", flexShrink: 0,
                }}>
                  {passed ? "Passed" : "Failed"}
                </Chip>
              </Stack>
              <Box sx={{
                width: "100%", height: "6px", backgroundColor: "rgba(255,255,255,0.08)",
                borderRadius: "3px", overflow: "hidden", display: "flex", mb: "8px",
              }}>
                <Box sx={{ width: `${yeaPct}%`, backgroundColor: "#4caf50" }} />
                <Box sx={{ width: `${nayPct}%`, backgroundColor: "#c1121f" }} />
              </Box>
              <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
                {yea} Yea · {nay} Nay
                {nv > 0 ? ` · ${nv} Not Voting` : ""}
                {absent > 0 ? ` · ${absent} Absent` : ""}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function HistoryTimeline({ history }) {
  const [expanded, setExpanded] = useState(false);
  if (!history || history.length === 0) return null;

  const sorted = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
  const PREVIEW = 4;
  const items = expanded ? sorted : sorted.slice(0, PREVIEW);
  const hasMore = sorted.length > PREVIEW;

  return (
    <Box>
      <Typography component="p" sx={{
        fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
        textTransform: "uppercase", letterSpacing: "0.12em", mb: "20px",
      }}>
        Legislative History
      </Typography>
      <Stack spacing="14px">
        {items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", gap: "20px" }}>
            <Typography sx={{
              fontSize: "12px", color: "rgba(255,255,255,0.38)",
              flexShrink: 0, minWidth: "88px", pt: "2px", fontVariantNumeric: "tabular-nums",
            }}>
              {formatDate(item.date)}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "15px", color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>
                {item.action}
              </Typography>
              {item.chamber && (
                <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.32)", mt: "2px" }}>
                  {item.chamber === "H" ? "House" : item.chamber === "S" ? "Senate" : item.chamber}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
      {hasMore && (
        <Button
          variant="plain" size="sm"
          endDecorator={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(p => !p)}
          sx={{
            mt: "16px", color: "#0075b2", fontWeight: 600, fontSize: "13px", px: 0,
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          {expanded ? "Show less" : `Show all ${sorted.length} actions`}
        </Button>
      )}
    </Box>
  );
}

function SponsorRow({ name, party, district, role }) {
  const partyLabel = party === "D" ? "Democrat" : party === "R" ? "Republican" : (party || "");
  const roleLabel = role === "Rep" ? "Representative" : role === "Sen" ? "Senator" : (role || "");
  const meta = [partyLabel, roleLabel, district].filter(Boolean).join(" · ");

  return (
    <Stack direction="row" spacing="12px" alignItems="center">
      <Avatar src={avatarFallback} alt={name || "Sponsor"} sx={{ width: 40, height: 40, flexShrink: 0 }}>
        {name ? name.charAt(0).toUpperCase() : "?"}
      </Avatar>
      <Box>
        <Typography sx={{ fontSize: "16px", fontWeight: 600, lineHeight: "24px", color: "#0075b2" }}>
          {name || "Unknown"}
        </Typography>
        {meta && (
          <Typography sx={{ fontSize: "14px", fontWeight: 400, lineHeight: "22px", color: "#535862" }}>
            {meta}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function StateBillDetail() {
  const params = new URLSearchParams(window.location.search);
  const billId = params.get("billId");
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sponsorsExpanded, setSponsorsExpanded] = useState(false);

  useEffect(() => {
    if (!billId) { setLoading(false); return; }
    fetch(`/api/state-bill/${billId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setBill(normalizeBill(data));
        document.title = `${data.title} | Civic Engage`;
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [billId]);

  const handleGoBack = () => {
    const state = params.get("state");
    if (state) {
      const url = new URL("/state-bills", window.location.origin);
      url.searchParams.set("state", state);
      window.history.pushState({}, "", url.toString());
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      window.history.back();
    }
  };

  if (loading) return <BillDetailSkeleton />;

  if (error || !bill) {
    return (
      <Box sx={{ maxWidth: "600px", mx: "auto", py: "80px", px: "24px", textAlign: "center" }}>
        <Box component="p" sx={{ fontSize: "18px", color: "#32383e", mb: "24px" }}>
          This bill couldn&apos;t be loaded. It may have moved or the link may be incorrect.
        </Box>
        <Box component="button" onClick={handleGoBack} sx={{
          background: "none", border: "none", cursor: "pointer",
          color: "#0075b2", fontWeight: 600, fontSize: "16px", textDecoration: "underline",
        }}>
          Back to State Bills
        </Box>
      </Box>
    );
  }

  const sponsors = Array.isArray(bill.sponsors) ? bill.sponsors : [];
  const primarySponsor = sponsors[0] || null;
  const additionalSponsors = sponsors.slice(1);
  const additionalCount = additionalSponsors.length;
  const whoItAffects = Array.isArray(bill.whoItAffects) ? bill.whoItAffects : [];
  const votes = Array.isArray(bill.votes) ? bill.votes : [];
  const history = Array.isArray(bill.history) ? bill.history : [];
  const subjects = Array.isArray(bill.subjects) ? bill.subjects : [];
  const progress = Array.isArray(bill.progress) ? bill.progress : [];
  const stateName = statesList[bill.state] || bill.state;
  const billTypeLabel = BILL_TYPE_LABELS[bill.billType] || "";

  const committee = bill.committee;
  const committeeDisplay = committee
    ? `${committee.chamber === "H" ? "House" : committee.chamber === "S" ? "Senate" : (committee.chamber || "")} ${committee.name || ""}`.trim()
    : null;

  return (
    <Box>
      {/* ── HERO ── */}
      <Box sx={{ position: "relative", backgroundColor: "#003049", overflow: "hidden" }}>
        <Box component="img" src={HeroImg} alt="" aria-hidden="true" sx={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", opacity: 0.2, pointerEvents: "none",
        }} />
        <Box sx={{
          maxWidth: "1200px", mx: "auto",
          px: { xs: "20px", md: "32px" }, py: "40px",
          position: "relative", zIndex: 1,
        }}>
          <Box component="nav" aria-label="Breadcrumb" sx={{ mb: "24px" }}>
            <Box component="button" onClick={handleGoBack} sx={{
              background: "none", border: "none", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: "6px",
              color: "rgba(255,255,255,0.6)", fontSize: "14px", fontWeight: 500, padding: 0,
              "&:hover": { color: "white" },
            }}>
              ← State Bills
            </Box>
          </Box>

          <Stack spacing="16px">
            <Typography component="p" sx={{
              fontSize: "16px", fontWeight: 600, color: "#fdf0d5",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {stateName}
              {bill.billNumber ? ` · ${bill.billNumber}` : ""}
              {bill.category ? ` · ${bill.category}` : ""}
            </Typography>

            <Typography level="h1" sx={{
              fontSize: { xs: "32px", md: "56px" }, fontWeight: 700,
              lineHeight: { xs: 1.25, md: 1.2 }, color: "white",
            }}>
              {bill.title}
            </Typography>

            {bill.description && (
              <Typography sx={{
                fontSize: "18px", fontWeight: 400, lineHeight: "26px",
                color: "rgba(255,255,255,0.9)", maxWidth: "720px",
              }}>
                {bill.description}
              </Typography>
            )}

            <Stack direction="row" spacing="12px" flexWrap="wrap">
              {bill.url && (
                <Button
                  component="a" href={bill.url} target="_blank" rel="noopener noreferrer"
                  endDecorator={<OpenInNewIcon />}
                  sx={{
                    width: "fit-content", backgroundColor: "white",
                    border: "1px solid #0075b2", color: "#0075b2",
                    fontWeight: 600, fontSize: "16px", borderRadius: "6px",
                    minHeight: "48px", px: "24px",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                  }}
                >
                  View on LegiScan
                </Button>
              )}
              {bill.stateLink && (
                <Button
                  component="a" href={bill.stateLink} target="_blank" rel="noopener noreferrer"
                  endDecorator={<OpenInNewIcon />}
                  sx={{
                    width: "fit-content", backgroundColor: "transparent",
                    border: "1px solid rgba(255,255,255,0.4)", color: "white",
                    fontWeight: 600, fontSize: "16px", borderRadius: "6px",
                    minHeight: "48px", px: "24px",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  View on State Legislature
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* ── CONTENT ── */}
      <Box sx={{ backgroundColor: "#003049", py: "40px" }}>
        <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: "20px", md: "32px" } }}>
          <Box sx={{
            display: "flex", flexDirection: { xs: "column", lg: "row" },
            gap: "32px", alignItems: "flex-start",
          }}>

            {/* ── LEFT COLUMN ── */}
            <Stack spacing="40px" sx={{ flex: 1, minWidth: 0 }}>

              {progress.length > 0 && <BillProgressState progress={progress} />}

              {subjects.length > 0 && (
                <Box>
                  <Typography component="p" sx={{
                    fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
                    textTransform: "uppercase", letterSpacing: "0.12em", mb: "14px",
                  }}>
                    Topics
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap="8px">
                    {subjects.map((s, i) => (
                      <Chip key={i} size="sm" sx={{
                        backgroundColor: "rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.75)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        fontSize: "13px", fontWeight: 500, borderRadius: "24px",
                      }}>
                        {s.subjectName || s.subject_name || s}
                      </Chip>
                    ))}
                  </Stack>
                </Box>
              )}

              {(whoItAffects.length > 0 || bill.whyItMatters) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "36px" }}>
                  {bill.whyItMatters && (
                    <Box>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
                        textTransform: "uppercase", letterSpacing: "0.12em", mb: "14px",
                      }}>
                        Why it matters
                      </Typography>
                      <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "30px", color: "rgba(255,255,255,0.9)" }}>
                        {bill.whyItMatters}
                      </Typography>
                    </Box>
                  )}
                  {whoItAffects.length > 0 && (
                    <Box>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#fdf0d5",
                        textTransform: "uppercase", letterSpacing: "0.12em", mb: "14px",
                      }}>
                        Who it impacts
                      </Typography>
                      <Box component="ul" sx={{ margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "6px" }}>
                        {whoItAffects.map((group, i) => (
                          <Box key={i} component="li" sx={{
                            fontSize: "18px", fontWeight: 400, lineHeight: "30px", color: "rgba(255,255,255,0.9)",
                          }}>
                            {group}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {(bill.support || bill.opposition) && (
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  borderRadius: "4px", overflow: "hidden",
                }}>
                  {bill.support && (
                    <Box sx={{
                      backgroundColor: "#f0f4f0", padding: "28px 24px",
                      borderRight: { sm: "1px solid rgba(0,0,0,0.06)" },
                      borderBottom: { xs: "1px solid rgba(0,0,0,0.06)", sm: "none" },
                    }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#2d5a2d",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "14px",
                      }}>
                        What supporters say
                      </Typography>
                      <Typography sx={{ fontSize: "17px", lineHeight: "28px", color: "#1a2e1a" }}>
                        {bill.support}
                      </Typography>
                    </Box>
                  )}
                  {bill.opposition && (
                    <Box sx={{ backgroundColor: "#fdf1f1", padding: "28px 24px" }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#7a2d2d",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "14px",
                      }}>
                        What the opposition says
                      </Typography>
                      <Typography sx={{ fontSize: "17px", lineHeight: "28px", color: "#2e1a1a" }}>
                        {bill.opposition}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {history.length > 0 && <HistoryTimeline history={history} />}
              

              {votes.length > 0 && <VoteResults votes={votes} />}

            </Stack>

            {/* ── RIGHT COLUMN (sticky) ── */}
            <Box sx={{
              width: { lg: "340px" }, flexShrink: 0,
              position: { lg: "sticky" }, top: { lg: "96px" },
              alignSelf: { lg: "flex-start" },
            }}>
              <Box sx={{
                backgroundColor: "white", borderRadius: "4px",
                boxShadow: "0px 1px 1px rgba(21,21,21,0.08), 0px 2px 2px rgba(21,21,21,0.08)",
                overflow: "hidden",
              }}>
                {/* Read full text */}
                <Box sx={{ padding: "24px" }}>
                  <Stack direction="row" spacing="6px" flexWrap="wrap" sx={{ mb: "12px" }}>
                    {bill.billNumber && (
                      <Chip size="sm" sx={{
                        backgroundColor: "#e5e7eb", color: "#374151",
                        fontWeight: 600, fontSize: "12px", borderRadius: "24px", border: "none",
                      }}>
                        {bill.billNumber}
                      </Chip>
                    )}
                    {bill.state && (
                      <Chip size="sm" sx={{
                        backgroundColor: "#e5e7eb", color: "#374151",
                        fontWeight: 500, fontSize: "12px", borderRadius: "24px", border: "none",
                      }}>
                        {stateName}
                      </Chip>
                    )}
                    {billTypeLabel && (
                      <Chip size="sm" sx={{
                        backgroundColor: "#e5e7eb", color: "#374151",
                        fontWeight: 500, fontSize: "12px", borderRadius: "24px", border: "none",
                      }}>
                        {billTypeLabel}
                      </Chip>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: "22px", fontWeight: 600, lineHeight: "30px", color: "#171a1c", mb: "8px" }}>
                    Read the full text
                  </Typography>
                  <Typography sx={{ fontSize: "15px", lineHeight: "24px", color: "#32383e", mb: "20px" }}>
                    View the official bill text on LegiScan or your state legislature&apos;s website.
                  </Typography>
                  <Stack spacing="10px">
                    {bill.url && (
                      <Button
                        fullWidth size="lg" component="a"
                        href={bill.url} target="_blank" rel="noopener noreferrer"
                        endDecorator={<OpenInNewIcon />}
                        sx={{
                          backgroundColor: "#0075b2", color: "white",
                          fontWeight: 600, fontSize: "16px", borderRadius: "6px", minHeight: "48px",
                          "&:hover": { backgroundColor: "#005c8e" },
                        }}
                      >
                        View on LegiScan
                      </Button>
                    )}
                    {bill.stateLink && (
                      <Button
                        fullWidth size="lg" component="a"
                        href={bill.stateLink} target="_blank" rel="noopener noreferrer"
                        endDecorator={<OpenInNewIcon />} variant="outlined"
                        sx={{
                          color: "#0075b2", borderColor: "#0075b2",
                          fontWeight: 600, fontSize: "16px", borderRadius: "6px", minHeight: "48px",
                          "&:hover": { backgroundColor: "rgba(0,117,178,0.06)" },
                        }}
                      >
                        State Legislature
                      </Button>
                    )}
                  </Stack>
                </Box>

                {committeeDisplay && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "20px 24px" }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "6px",
                      }}>
                        Committee
                      </Typography>
                      <Typography sx={{ fontSize: "15px", lineHeight: "24px", color: "#32383e" }}>
                        {committeeDisplay}
                      </Typography>
                    </Box>
                  </>
                )}

                {bill.lastActionDescription && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "20px 24px" }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "8px",
                      }}>
                        Current Status
                      </Typography>
                      <Typography sx={{ fontSize: "15px", lineHeight: "24px", color: "#32383e" }}>
                        {bill.lastActionDescription}
                      </Typography>
                      {bill.lastActionDate && (
                        <Typography sx={{ fontSize: "13px", color: "#9ca3af", mt: "4px" }}>
                          {formatDate(bill.lastActionDate)}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}

                {bill.sessionId && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "16px 24px" }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "4px",
                      }}>
                        Session
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#535862" }}>
                        {bill.sessionId}
                      </Typography>
                    </Box>
                  </>
                )}

                {primarySponsor && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "20px 24px" }}>
                      <Typography component="p" sx={{
                        fontSize: "11px", fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.1em", mb: "12px",
                      }}>
                        Primary Sponsor
                      </Typography>
                      <SponsorRow
                        name={primarySponsor.name}
                        party={primarySponsor.party || primarySponsor.partyId}
                        district={primarySponsor.district}
                        role={primarySponsor.role}
                      />
                      {additionalCount > 0 && (
                        <>
                          {sponsorsExpanded && (
                            <Stack spacing="12px" sx={{ mt: "12px" }}>
                              {additionalSponsors.slice(0, 5).map((s, i) => (
                                <SponsorRow
                                  key={i}
                                  name={s.name}
                                  party={s.party || s.partyId}
                                  district={s.district}
                                  role={s.role}
                                />
                              ))}
                              {additionalCount > 5 && (
                                <Typography sx={{ fontSize: "13px", color: "#9ca3af" }}>
                                  +{additionalCount - 5} more cosponsor{additionalCount - 5 !== 1 ? "s" : ""}
                                </Typography>
                              )}
                            </Stack>
                          )}
                          <Button
                            variant="plain" size="sm"
                            endDecorator={sponsorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setSponsorsExpanded(p => !p)}
                            sx={{
                              mt: "10px", color: "#0075b2", fontWeight: 600, fontSize: "13px", px: 0,
                              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                            }}
                          >
                            {sponsorsExpanded
                              ? "Show less"
                              : `+${additionalCount} cosponsor${additionalCount !== 1 ? "s" : ""}`}
                          </Button>
                        </>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default StateBillDetail;
